/**
 * What: this is a context provider and wraps each page
 * Why: need to provide page information for tracking
 * How: based on received props it computes values needed in other contexts &| tracking handler
 */
import {TrackingPageContext} from '@aboutyou/legacy-tracking-sdk';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

const PageTrackingContext = createContext<TrackingPageContext>(
  {} as TrackingPageContext,
);
const PageTrackingContextCallback = createContext<() => TrackingPageContext>(
  () => ({} as TrackingPageContext),
);

/**
 * This will re-render as soon as the page context changes. If you have to rely on changes on render, use this hook. This is in most cases not required, so usage of useTrackingPageContextCallback is recommended to avoid re-rerenders due to tracking.
 */
export const useTrackingPageContext = () => useContext(PageTrackingContext);
/**
 * Returns a function that returns the current page context. Should be used whenever the page context is only used as a side effect and it does not have to re-render components.
 */
export const useTrackingPageContextCallback = () =>
  useContext(PageTrackingContextCallback);

// returns the context based on env
export const PageTrackingContextProvider: FC<
  React.PropsWithChildren<TrackingPageContext>
> = props => {
  if (process.browser) {
    return <PageTrackingContextProviderClient {...props} />;
  } else {
    return <PageTrackingContextProviderServer {...props} />;
  }
};

const PageTrackingContextProviderServer: FC<React.PropsWithChildren> = ({
  children,
}) => <>{children}</>;

const PageTrackingContextProviderClient: FC<
  React.PropsWithChildren<TrackingPageContext>
> = ({children, ...props}) => {
  const {path, search, type, id} = props;
  const pageContextValue = useMemo(
    () => ({path, search, type, id} as TrackingPageContext),
    [path, search, type, id],
  );
  const mutableContextValue = useRef<TrackingPageContext>(pageContextValue);
  // update the reference on every change
  mutableContextValue.current = pageContextValue;
  const pageTrackingContextCallback = useCallback(
    () => mutableContextValue.current,
    [mutableContextValue],
  );

  return (
    <PageTrackingContext.Provider
      value={pageContextValue as TrackingPageContext}
    >
      <PageTrackingContextCallback.Provider value={pageTrackingContextCallback}>
        {children}
      </PageTrackingContextCallback.Provider>
    </PageTrackingContext.Provider>
  );
};
